import React, { FC, Fragment } from 'react';
import {
  LegendGradient,
  LegendValues,
  LegendValuesSet,
  LegendValuesSetsWrapper,
  OneValueColor,
  OneValueVal,
  OneValueWrapper,
  MapLegendWrapper,
  GradientsWrapper,
  LegendValueZero,
} from './styles';
import {
  AXIS_FORMAT_THRESHOLD,
  formatBigNumber,
  getFormattedValue,
  NUMBER_FORMAT_TYPES,
  transformNumber
} from "../../../../utils/format";
import { CONFIG_ELEMENT_VALUES_TYPE } from "../../../../gql/widget/types";

const getLegendValues = (
  max: number,
  min: number,
  isFromMin: boolean,
  steps: number,
  negative?: boolean,
) => {
  const showDecimals = Math.abs(max) <= 10;
  const stepsNum = steps - 2;
  const legendMin = isFromMin ? min : 0;

  let legendStep = (max - legendMin) / (steps - 1);

  if (!showDecimals) {
    legendStep = Math.round(legendStep);
  }

  let step = max;

  return [
    max,
    ...Array(stepsNum)
      .fill(null)
      .map(() => (step -= legendStep))
      .map((val) => {
        if (!negative && val < 0) {
          val = 0;
        }

        if (negative && val >= 0) {
          val = 0;
        }

        return showDecimals ? parseFloat(val.toFixed(2)) : Math.round(val);
      }),
    legendMin,
  ];
};

const roundForLegend = (s: string, type: CONFIG_ELEMENT_VALUES_TYPE) => {
  const isNumberFormat = NUMBER_FORMAT_TYPES.includes(type);

  const [number, ...rest] = s.split(' ');

  let n = parseFloat(
    isNumberFormat ? number.replace(/\./g, '').replace(/,/g, '.') : number,
  );

  const absN = Math.abs(n);

  if (absN < 10) {
    n = Math.round(n * 10) / 10;
  } else if (absN < 100) {
    n = Math.round(n);
  } else {
    n = Math.round(n / 10) * 10;
  }

  return `${transformNumber(String(n))}${
    rest.length ? ` ${rest.join(' ')}` : ''
  }`;
};

const mapLegendValues = (
  values: number[],
  shouldFormat: boolean,
  dataType: CONFIG_ELEMENT_VALUES_TYPE,
) =>
  values.map((val, ind) => {
    const renderValue = shouldFormat
      ? formatBigNumber(val, dataType)
      : getFormattedValue(String(val), dataType);

    return (
      <div key={`${ind}-${val}`}>{roundForLegend(renderValue, dataType)}</div>
    );
  });

type OneValueProps = {
  value: number;
  color: string;
  dataType: CONFIG_ELEMENT_VALUES_TYPE;
};

const OneValue: FC<OneValueProps> = ({ value, color, dataType }) => {
  const renderValue =
    value > AXIS_FORMAT_THRESHOLD
      ? formatBigNumber(value)
      : value.toFixed(value % 1 !== 0 ? 2 : 0);

  return (
    <OneValueWrapper>
      <OneValueColor color={color} />
      <OneValueVal>
        {roundForLegend(getFormattedValue(renderValue, dataType), dataType)}
      </OneValueVal>
    </OneValueWrapper>
  );
};

type Props = {
  positiveColor: string;
  negativeColor: string;
  isFromMin: boolean;
  neutralColor: string;
  originalValues: number[];
  minPositiveColor: string;
  minNegativeColor: string;
  hasBoth: boolean;
  hasPositive: boolean;
  hasNegative: boolean;
  onePositive: boolean;
  oneNegative: boolean;
  positives: number[];
  negatives: number[];
  dataType: CONFIG_ELEMENT_VALUES_TYPE;
};

const MapLegend: FC<Props> = ({
  positiveColor,
  negativeColor,
  isFromMin,
  neutralColor,
  originalValues,
  minNegativeColor,
  minPositiveColor,
  hasBoth,
  hasPositive,
  hasNegative,
  onePositive,
  positives,
  oneNegative,
  negatives,
  dataType,
}) => {
  let legendValuesStepsCount = 5;

  if (!isFromMin && hasBoth) {
    legendValuesStepsCount = 3;
  }

  const max = Math.max(...originalValues);
  const min = Math.min(...originalValues);

  const negativeMin = Math.max(...negatives);
  const positiveMin = Math.min(...positives);

  const isSingleValue = max === min;
  let singleValueColor;
  if (max === 0) {
    singleValueColor = minPositiveColor;
  } else {
    singleValueColor = max < 0 ? negativeColor : positiveColor;
  }

  const shouldFormat =
    max > AXIS_FORMAT_THRESHOLD || Math.abs(min) > AXIS_FORMAT_THRESHOLD;

  const legendPositiveValues = getLegendValues(
    max,
    positiveMin,
    isFromMin,
    legendValuesStepsCount,
  );
  const legendNegativeValues = getLegendValues(
    min,
    negativeMin,
    isFromMin,
    legendValuesStepsCount,
    true,
  );

  const positiveLegendStartsWithZero =
    hasPositive && legendPositiveValues[legendPositiveValues.length - 1] === 0;
  const negativeLegendStartsWithZero =
    hasNegative && legendNegativeValues[legendNegativeValues.length - 1] === 0;

  const shouldIncludeZeroValue =
    positiveLegendStartsWithZero && negativeLegendStartsWithZero;

  if (shouldIncludeZeroValue) {
    legendPositiveValues.pop();
    legendNegativeValues.pop();
  }

  const FromZeroValues = () => {
    if (hasBoth) {
      return (
        <LegendValuesSetsWrapper>
          <LegendValuesSet>
            {mapLegendValues(legendPositiveValues, shouldFormat, dataType)}
          </LegendValuesSet>
          {shouldIncludeZeroValue && (
            <LegendValueZero
              surroundingElementsCount={
                legendPositiveValues.length + legendNegativeValues.length
              }
            >
              {mapLegendValues([0], shouldFormat, dataType)}
            </LegendValueZero>
          )}
          <LegendValuesSet negative>
            {mapLegendValues(legendNegativeValues, shouldFormat, dataType)}
          </LegendValuesSet>
        </LegendValuesSetsWrapper>
      );
    }

    return (
      <LegendValues negative={!hasPositive}>
        {mapLegendValues(
          hasPositive ? legendPositiveValues : legendNegativeValues,
          shouldFormat,
          dataType,
        )}
      </LegendValues>
    );
  };

  return (
    <Fragment>
      <MapLegendWrapper double={isFromMin && hasBoth}>
        {isSingleValue ? (
          <OneValue value={max} color={singleValueColor} dataType={dataType} />
        ) : (
          <Fragment>
            <GradientsWrapper>
              {!isFromMin ? (
                <Fragment>
                  <LegendGradient
                    minColor={hasNegative ? negativeColor : minPositiveColor}
                    maxColor={hasPositive ? positiveColor : minNegativeColor}
                    neutralColor={hasBoth ? neutralColor : undefined}
                  />
                  <FromZeroValues />
                </Fragment>
              ) : (
                <Fragment>
                  {hasPositive && (
                    <Fragment>
                      <LegendGradient
                        minColor={minPositiveColor}
                        maxColor={positiveColor}
                      />
                      <LegendValues>
                        {mapLegendValues(
                          legendPositiveValues,
                          shouldFormat,
                          dataType,
                        )}
                      </LegendValues>
                    </Fragment>
                  )}
                  <LegendValues>
                    {shouldIncludeZeroValue &&
                      mapLegendValues([0], shouldFormat, dataType)}
                  </LegendValues>
                  {hasNegative && (
                    <Fragment>
                      <LegendGradient
                        minColor={minNegativeColor}
                        maxColor={negativeColor}
                      />
                      <LegendValues>
                        {mapLegendValues(
                          legendNegativeValues,
                          shouldFormat,
                          dataType,
                        )}
                      </LegendValues>
                    </Fragment>
                  )}
                </Fragment>
              )}
            </GradientsWrapper>
            {onePositive && (
              <OneValue
                value={positives[0]}
                color={positiveColor}
                dataType={dataType}
              />
            )}
            {oneNegative && (
              <OneValue
                value={negatives[0]}
                color={negativeColor}
                dataType={dataType}
              />
            )}
          </Fragment>
        )}
      </MapLegendWrapper>
    </Fragment>
  );
};

export default MapLegend;
